import { type RecurringInterval } from "@fitness-app/data-models/entities/Product";

/**
 * Does an installment price bill one installment per billing period?
 *
 * `installments: N` says HOW MANY payments; the billing interval says HOW OFTEN.
 * Every consumer downstream assumes one installment per interval:
 * `HandleSubscriptionCycleUseCase` builds the Stripe schedule with
 * `iterations: installments - 1` in the price's own cadence, and the plan's end is
 * derived by adding `installments` intervals to its start.
 *
 * So a price that carries both `installments: 12` and `interval_count: 12` does not
 * mean "12 months of access paid in 12 parts" — it means one 547 PLN charge every
 * twelve months, twelve times over, i.e. a 144-month plan whose first installment
 * buys a whole year of access (TRE-1653, one client). The admin form makes this easy
 * to hit: the recurring field is labelled "billing period", and "custom: 12 months"
 * reads like "12 months of access".
 *
 * Weeks are left alone on purpose — "6-week challenge, 2 installments 3 weeks apart"
 * (`week` / `interval_count: 3`) is a real product and bills correctly.
 */

export type InstallmentCadenceProblem = {
  code: "interval_count_must_be_one";
  installments: number;
  interval: RecurringInterval;
  intervalCount: number;
  /** How long the plan would actually run, in whole intervals. */
  planLength: number;
};

type PriceCadence = {
  installments?: number | null;
  interval?: RecurringInterval | null;
  interval_count?: number | null;
};

const CADENCE_LOCKED_TO_ONE: RecurringInterval[] = ["month", "year"];

/**
 * The cadence a price describes, or null when it is not an installment plan at all.
 * Shared so the detector and the description can never disagree on what defaults to
 * apply — a divergence there would defeat the point of this module.
 */
const resolveCadence = (price: PriceCadence) => {
  const installments = price.installments ?? 0;
  const interval = price.interval ?? null;

  if (installments < 2 || !interval) {
    return null;
  }

  return { installments, interval, intervalCount: price.interval_count ?? 1 };
};

export const getInstallmentCadenceProblem = (price: PriceCadence): InstallmentCadenceProblem | null => {
  const cadence = resolveCadence(price);

  if (!cadence) {
    return null;
  }

  const { installments, interval, intervalCount } = cadence;

  if (intervalCount === 1 || !CADENCE_LOCKED_TO_ONE.includes(interval)) {
    return null;
  }

  return {
    code: "interval_count_must_be_one",
    installments,
    interval,
    intervalCount,
    planLength: installments * intervalCount,
  };
};

/**
 * The plan a price describes, for a confirmation line in the form — seeing
 * "12 installments every 12 months (plan runs 144 months)" is what makes the
 * mistake obvious before it is saved.
 */
export const describeInstallmentPlan = (price: PriceCadence) => {
  const cadence = resolveCadence(price);

  if (!cadence) {
    return null;
  }

  return {
    ...cadence,
    /** Total plan length expressed in `interval` units. */
    planLength: cadence.installments * cadence.intervalCount,
  };
};
